/**
 * useSwo.ts
 *
 * Typed API hooks for Service Work Order (SWO) module.
 * Uses the OpenAPI-generated typed client from @jinzhuan/api-client.
 */
import { useState, useCallback } from 'react';
import { createApiClient, ApiError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 服务工单端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ─── DTO type aliases ────────────────────────────────────────────────────────
export type ServiceWorkOrderDto = ApiComponents['schemas']['ServiceWorkOrderDto'];
export type CreateServiceWorkOrderDto = ApiComponents['schemas']['CreateServiceWorkOrderDto'];
export type UpdateServiceWorkOrderDto = ApiComponents['schemas']['UpdateServiceWorkOrderDto'];
export type AssignWorkOrderDto = ApiComponents['schemas']['AssignWorkOrderDto'];
export type BlockWorkOrderDto = ApiComponents['schemas']['BlockWorkOrderDto'];
export type CancelWorkOrderDto = ApiComponents['schemas']['CancelWorkOrderDto'];
export type StartWorkOrderDto = ApiComponents['schemas']['StartWorkOrderDto'];
export type UnblockWorkOrderDto = ApiComponents['schemas']['UnblockWorkOrderDto'];
export type CompleteWorkOrderDto = ApiComponents['schemas']['CompleteWorkOrderDto'];

// ─── Status / Priority labels ─────────────────────────────────────────────────
export const WORK_ORDER_STATUS_LABELS: Record<string, string> = {
  open: '待处理',
  in_progress: '进行中',
  blocked: '已阻塞',
  completed: '已完成',
  cancelled: '已取消',
};

export const WORK_ORDER_PRIORITY_LABELS: Record<string, string> = {
  low: '低',
  normal: '普通',
  high: '高',
  urgent: '紧急',
};

export const WORK_ORDER_TYPE_LABELS: Record<string, string> = {
  follow_up: '跟进',
  health_assessment: '健康评估',
  infusion_prep: '回输准备',
  document_collection: '资料收集',
  other: '其他',
};

export const WORK_ORDER_STATUS_LIST = ['open', 'in_progress', 'blocked', 'completed', 'cancelled'] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUS_LIST)[number];

// ─── Shared error types ───────────────────────────────────────────────────────
export interface SwoApiError {
  message: string;
  code?: string;
  status?: number;
}

function thrownToApiError(err: unknown): SwoApiError {
  if (err instanceof ApiError) {
    let message = err.message;
    if (err.status === 401) {
      message = '会话已失效，请重新登录后再试';
    } else if (err.status === 403) {
      message = '当前角色没有服务工单访问权限';
    } else if (err.status === 409) {
      message = '数据已被其他用户修改，请刷新页面后重试';
    }
    return { message, code: err.code, status: err.status };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useWorkOrderList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<SwoApiError | null>(null);

  const fetch = useCallback(
    async (params: {
      status?: WorkOrderStatus;
      assigneeId?: string;
      customerId?: string;
      page?: number;
      limit?: number;
    }): Promise<{ items: readonly ServiceWorkOrderDto[]; total: number } | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/operations/work-orders', {
          params: {
            query: {
              status: params.status,
              assigneeId: params.assigneeId,
              customerId: params.customerId,
              page: params.page,
              limit: params.limit,
            },
          },
        });
        if (apiErr) {
          setError({ message: String(apiErr) });
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, fetch };
}

export function useWorkOrderDetail() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<SwoApiError | null>(null);

  const fetch = useCallback(async (id: string): Promise<ServiceWorkOrderDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.GET('/api/v1/operations/work-orders/{id}', {
        params: { path: { id } },
      });
      if (apiErr) {
        setError({ message: String(apiErr) });
        return null;
      }
      return data ?? null;
    } catch (err) {
      setError(thrownToApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, fetch };
}

export function useWorkOrderCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<SwoApiError | null>(null);

  const create = useCallback(async (dto: CreateServiceWorkOrderDto): Promise<ServiceWorkOrderDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST('/api/v1/operations/work-orders', {
        body: dto,
      });
      if (apiErr) {
        setError({ message: String(apiErr) });
        return null;
      }
      return data ?? null;
    } catch (err) {
      setError(thrownToApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, create };
}

export function useWorkOrderUpdate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<SwoApiError | null>(null);

  const update = useCallback(
    async (id: string, dto: UpdateServiceWorkOrderDto): Promise<ServiceWorkOrderDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.PUT('/api/v1/operations/work-orders/{id}', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError({ message: String(apiErr) });
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, update };
}

export function useWorkOrderTransition() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<SwoApiError | null>(null);

  const start = useCallback(async (id: string, expectedVersion: number): Promise<ServiceWorkOrderDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST('/api/v1/operations/work-orders/{id}/start', {
        params: { path: { id } },
        body: { expectedVersion } as StartWorkOrderDto,
      });
      if (apiErr) {
        setError({ message: String(apiErr) });
        return null;
      }
      return data ?? null;
    } catch (err) {
      setError(thrownToApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const block = useCallback(async (id: string, dto: BlockWorkOrderDto): Promise<ServiceWorkOrderDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST('/api/v1/operations/work-orders/{id}/block', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError({ message: String(apiErr) });
        return null;
      }
      return data ?? null;
    } catch (err) {
      setError(thrownToApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const unblock = useCallback(async (id: string, expectedVersion: number): Promise<ServiceWorkOrderDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST('/api/v1/operations/work-orders/{id}/unblock', {
        params: { path: { id } },
        body: { expectedVersion } as UnblockWorkOrderDto,
      });
      if (apiErr) {
        setError({ message: String(apiErr) });
        return null;
      }
      return data ?? null;
    } catch (err) {
      setError(thrownToApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const complete = useCallback(async (id: string, expectedVersion: number): Promise<ServiceWorkOrderDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST('/api/v1/operations/work-orders/{id}/complete', {
        params: { path: { id } },
        body: { expectedVersion } as CompleteWorkOrderDto,
      });
      if (apiErr) {
        setError({ message: String(apiErr) });
        return null;
      }
      return data ?? null;
    } catch (err) {
      setError(thrownToApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const cancel = useCallback(async (id: string, dto: CancelWorkOrderDto): Promise<ServiceWorkOrderDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST('/api/v1/operations/work-orders/{id}/cancel', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError({ message: String(apiErr) });
        return null;
      }
      return data ?? null;
    } catch (err) {
      setError(thrownToApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const assign = useCallback(async (id: string, dto: AssignWorkOrderDto): Promise<ServiceWorkOrderDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST('/api/v1/operations/work-orders/{id}/assign', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError({ message: String(apiErr) });
        return null;
      }
      return data ?? null;
    } catch (err) {
      setError(thrownToApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, start, block, unblock, complete, cancel, assign };
}
